import gsap from 'gsap';
import { module as Module } from 'modujs';
import { FONT } from '../config';
import { whenReady } from '../utils/fonts';
import { $screenDebounce } from '../stores/screen';
import { SplitText } from 'gsap/SplitText';

gsap.registerPlugin(SplitText);

export default class HeroWords extends Module {
    static INTERVAL = 3;

    constructor(m) {
        super(m);

        // Binding
        this.onResize = this.onResize.bind(this);
        this.onUpdate = this.onUpdate.bind(this);
        this.reSplit = this.reSplit.bind(this);

        // UI
        this.$items = Array.from(this.$('item'));
        this.$ref = this.el.previousElementSibling;

        // Data
        this.activeIndex = 0;
        this.prevIndex = -1;
        this.interval = null;
        this.isFirstSplitting = false;
        this.wWidth = -1;
    }

    // =============================================================================
    // Lifecycle
    // =============================================================================
    init() {
        whenReady(FONT.EAGER).then((fonts) => this.onFontsLoaded(fonts));
        this.bindEvents();

        requestAnimationFrame(() => {
            this.setReferenceHeight();
            this.reSplit();
        });
    }

    destroy() {
        super.destroy();
        this.unbindEvents();
    }

    // =============================================================================
    // Events
    // =============================================================================
    bindEvents() {
        this.unbindScreenListener = $screenDebounce.listen(this.onResize);
    }

    unbindEvents() {
        this.unbindScreenListener?.();
        this.interval?.kill();
    }

    // =============================================================================
    // Callbacks
    // =============================================================================
    onFontsLoaded(fonts) {
        this.setReferenceHeight();
        this.reSplit();
    }

    onResize({ width, height }) {
        if (this.wWidth === width) return;

        this.wWidth = width;
        requestAnimationFrame(() => {
            this.setReferenceHeight();
            this.reSplit();
        });
    }

    onUpdate() {
        this.interval = gsap.delayedCall(HeroWords.INTERVAL, this.onUpdate);

        this.update();
    }

    // =============================================================================
    // Methods
    // =============================================================================
    setReferenceHeight() {
        const maxHeight = Math.max(...this.$items.map(($item) => $item.offsetHeight));
        this.$ref.style.height = `${maxHeight}px`;
    }

    split() {
        this.splitObject = new SplitText(this.$items, {
            type: 'words',
            tag: 'span'
        });

        const wordByItems = [];
        this.splitObject.words.forEach((word, i) => {
            this.$items.forEach(($item, itemsIndex) => {
                if ($item.contains(word)) {
                    if (!wordByItems[itemsIndex]) {
                        wordByItems[itemsIndex] = [];
                    }
                    wordByItems[itemsIndex].push(word);
                }
            });
        });

        wordByItems.forEach((words) => {
            words.forEach((word, wordIndex) => {
                word.style.setProperty('--word-index', wordIndex);
            });
        });
    }

    reSplit() {
        this.splitObject?.revert?.();

        requestAnimationFrame(() => {
            this.split();

            if (!this.isFirstSplitting) {
                this.isFirstSplitting = true;
                this.onUpdate();
            }
        });
    }

    update() {
        this.$items[this.prevIndex]?.classList.remove('is-prev');

        this.prevIndex = this.activeIndex;
        this.activeIndex = (this.activeIndex + 1) % this.$items.length;

        this.$items[this.prevIndex]?.classList.remove('is-active');
        this.$items[this.activeIndex]?.classList.remove('is-prev');

        this.$items[this.prevIndex]?.classList.add('is-prev');
        this.$items[this.activeIndex]?.classList.add('is-active');
    }
}
